import { PlayerSiteLocale } from "constants/player-locales"
import { Pagination } from "constants/type"

export type CmsPageType = "page" | "article" | "landing"
export type CmsPageStatus = "draft" | "scheduled" | "published"

/** "auto" follows the indexing toggle; the other two force the decision. */
export type CmsSitemapInclude = "auto" | "yes" | "no"

export type CmsChangeFrequency = "always" | "hourly" | "daily" | "weekly" | "monthly" | "yearly" | "never"

export type CmsTwitterCard = "summary" | "summary_large_image"
export type CmsOpenGraphType = "website" | "article"

export interface CmsSeoScore {
  passed: number
  total: number
}

export interface CmsPageListItem {
  id: string
  locale: string
  /** Locale-less route path, normalised: leading slash, no trailing slash ("/" = home). */
  path: string
  slug: string
  title: string
  type: CmsPageType
  status: CmsPageStatus
  robots_index: boolean
  seo_score: CmsSeoScore
  /** Locales this page is translated into. */
  locales: string[]
  /** Locales the brand serves that this page is still missing. */
  missing_locales: string[]
  category: string | null
  author: string | null
  published_at: string | null
  updated_at: string
  updated_by: string | null
}

export interface CmsFaqEntry {
  question: string
  answer: string
}

/** One language version of a page, as listed on its siblings. */
export interface CmsTranslation {
  id: string
  path: string
  status: CmsPageStatus
}

/** Server-computed cache facts shown in the editor rail. */
export interface CmsRendering {
  revalidate_seconds: number
  cache_purged_at: string | null
}

export interface CmsPage {
  id: string
  tenant_id: string
  locale: string
  path: string
  slug: string
  parent_id: string | null

  type: CmsPageType
  status: CmsPageStatus
  template: string
  category: string | null
  author: string | null
  sort_order: number

  title: string
  h1: string
  body_html: string
  /** SEO copy below the page content, collapsible by the visitor. */
  footer_html: string
  footer_collapsed: boolean
  faq: CmsFaqEntry[]

  seo_title: string
  seo_description: string
  canonical: string | null
  robots_index: boolean
  robots_follow: boolean
  robots_advanced: string | null

  sitemap_include: CmsSitemapInclude
  sitemap_priority: number
  sitemap_changefreq: CmsChangeFrequency

  og_title: string | null
  og_description: string | null
  og_image: string | null
  og_type: CmsOpenGraphType
  twitter_card: CmsTwitterCard

  schema_override: string | null
  head_code: string | null
  body_code: string | null

  exclude_from_search: boolean
  password_protect: boolean

  /** Locale -> sibling, including drafts. Only translated locales appear. */
  translations: Record<string, CmsTranslation>

  rendering: CmsRendering

  published_at: string | null
  created_at: string
  updated_at: string
  updated_by: string | null
}

export type CmsPageInput = Omit<CmsPage, "id" | "tenant_id" | "created_at" | "updated_at" | "updated_by" | "translations" | "rendering">

export interface CmsPageListQuery {
  page: number
  pageSize: number
  locale?: string
  status?: CmsPageStatus
  type?: CmsPageType
  category?: string
  author?: string
  q?: string
}

export type CmsPageListResponse = Pagination & { items: CmsPageListItem[] }

export interface CmsPageVersion {
  version: number
  created_at: string
  created_by: string | null
  note: string | null
}

export interface CmsSlugCheck {
  available: boolean
  /** Present when the slug collides with a reserved or generated route. */
  reason: string | null
}

export interface CmsDuplicateInput {
  to_locale?: string
  to_tenant?: string
}

/** Title/robots templates applied to routes the platform generates rather than authors. */
export interface CmsSeoRule {
  id: string
  priority: number
  name: string
  url_pattern: string
  title_template: string | null
  description_template: string | null
  robots_index: boolean
  match_count: number
  active: boolean
  created_at: string
  updated_at: string
}

export type CmsSeoRuleInput = Omit<CmsSeoRule, "id" | "match_count" | "created_at" | "updated_at">

export interface CmsSeoRuleTestResult {
  matched: boolean
  rule_id: string | null
  rule_name: string | null
  title: string | null
  description: string | null
  robots_index: boolean
}

export type CmsRedirectCode = 301 | 302

export type CmsRedirectOrigin = "manual" | "slug_change" | "bulk_import" | "expired_event"

export interface CmsRedirect {
  id: string
  source: string
  target: string
  code: CmsRedirectCode
  origin: CmsRedirectOrigin
  hits_30d: number
  last_hit_at: string | null
  created_at: string
  updated_at: string
}

export type CmsRedirectInput = Pick<CmsRedirect, "source" | "target" | "code">

export interface CmsRedirectImportResult {
  imported: number
  skipped: number
  errors: string[]
}

export interface CmsMediaItem {
  id: string
  url: string
  filename: string
  alt: string
  width: number
  height: number
  size_bytes: number
  mime_type: string
  /** Paths of the pages referencing this file. */
  used_by: string[]
  created_at: string
}

export type CmsMediaListResponse = Pagination & { items: CmsMediaItem[] }

export interface CmsSitemapChild {
  name: string
  url: string
  url_count: number
}

export interface CmsSitemapSummary {
  robots_url: string
  children: CmsSitemapChild[]
  last_generated_at: string | null
}

export interface CmsRobots {
  body: string
  updated_at: string | null
  updated_by: string | null
}

export const CMS_PAGE_TYPES: CmsPageType[] = ["page", "article", "landing"]
export const CMS_PAGE_STATUSES: CmsPageStatus[] = ["draft", "scheduled", "published"]

export const CMS_CHANGE_FREQUENCIES: CmsChangeFrequency[] = ["always", "hourly", "daily", "weekly", "monthly", "yearly", "never"]

export const CMS_SITEMAP_PRIORITIES = ["1.0", "0.9", "0.8", "0.7", "0.6", "0.5", "0.4", "0.3"] as const

export const CMS_TEMPLATES = ["default", "article", "landing-a", "landing-b"] as const

/** Re-exported so callers in this module have one import for its types and its locales. */
export { PLAYER_LOCALE_NAMES as CMS_LOCALE_NAMES, PLAYER_LOCALES as CMS_LOCALES } from "constants/player-locales"

export type CmsLocale = PlayerSiteLocale

/** Google truncates past these; exceeding them is a warning, not an error. */
export const SEO_TITLE_LIMIT = 60
export const SEO_DESCRIPTION_LIMIT = 160
export const SEO_DESCRIPTION_MIN = 50

/** How a brand shapes its URLs; the panel cannot derive this from its own build. */
export type LocalePrefixMode = "always" | "as-needed"

/** Wire shape of GET /admin/cms/site-url. */
export interface BrandSiteConfigResponse {
  url: string | null
  locale_prefix?: string
  default_locale?: string
}

export interface BrandSiteConfig {
  url: string | null
  localePrefix: LocalePrefixMode
  defaultLocale: string
}
